import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackButton } from '../components/BackButton';
import { ErrorMessage } from '../components/LoadingGrid';
import { api } from '../lib/api-client';
import { chapterIdForUrl } from '../lib/chapter-id';
import { useCurrentUser } from '../lib/hooks';
import { useLang } from '../lib/lang';
import type { Chapter, DramaDetail } from '../lib/types';

/** Tombol favorit — aktif hanya bila login Telegram terdeteksi. */
function FavoriteButton({
  platform,
  dramaId,
  title,
  poster,
  episodeCount,
}: {
  platform: string;
  dramaId: string;
  title: string;
  poster: string | null;
  episodeCount: number | null;
}) {
  const qc = useQueryClient();
  const meQuery = useCurrentUser();
  const loggedIn = (meQuery.data?.user ?? null) !== null;

  const favStatus = useQuery({
    queryKey: ['isFavorite', platform, dramaId],
    queryFn: () => api.isFavorite(platform, dramaId),
    enabled: loggedIn,
  });
  const isFav = favStatus.data?.favorite ?? false;

  const toggle = useMutation({
    mutationFn: async () => {
      if (isFav) {
        await api.removeFavorite(platform, dramaId);
      } else {
        await api.addFavorite({
          platform,
          dramaId,
          title,
          ...(poster ? { poster } : {}),
          ...(episodeCount !== null ? { episodeCount } : {}),
        });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['isFavorite', platform, dramaId] });
      void qc.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  return (
    <button
      type="button"
      onClick={() => loggedIn && toggle.mutate()}
      disabled={!loggedIn || toggle.isPending}
      title={loggedIn ? undefined : 'Buka lewat Telegram untuk menyimpan favorit'}
      className="flex min-h-[56px] flex-col items-center justify-center gap-1.5 rounded-xl border border-line/[0.07] bg-ink-900 disabled:opacity-50"
    >
      <svg
        className={isFav ? 'text-brand-500' : 'text-neutral-300'}
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill={isFav ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 20.5l-1.4-1.3C5.4 14.5 2 11.4 2 7.6 2 4.9 4.1 3 6.7 3c1.6 0 3.1.8 4 2 .9-1.2 2.4-2 4-2C17.9 3 20 4.9 20 7.6c0 3.8-3.4 6.9-8.6 11.6l-1.4 1.3z" />
      </svg>
      <span className={`text-[11px] font-semibold ${isFav ? 'text-brand-500' : 'text-neutral-300'}`}>
        {isFav ? 'Tersimpan' : 'Daftar Saya'}
      </span>
    </button>
  );
}

/** Bagikan tautan judul — pakai share sheet bila ada, jatuh ke clipboard. */
function ShareButton({ title }: { title: string }) {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // dibatalkan user / clipboard ditolak — tidak perlu ganggu.
    }
  };
  return (
    <button
      type="button"
      onClick={() => void share()}
      className="flex min-h-[56px] flex-col items-center justify-center gap-1.5 rounded-xl border border-line/[0.07] bg-ink-900"
    >
      <svg className="text-neutral-300" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
        <path d="M12 15V3M8 7l4-4 4 4" />
      </svg>
      <span className="text-[11px] font-semibold text-neutral-300">
        {copied ? 'Tersalin' : 'Bagikan'}
      </span>
    </button>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-[300px] w-full bg-ink-900" />
      <div className="px-4">
        <div className="mt-4 h-7 w-3/4 rounded bg-ink-800" />
        <div className="mt-3 h-4 w-1/3 rounded bg-ink-800" />
        <div className="mt-5 h-[50px] rounded-xl bg-ink-800" />
      </div>
    </div>
  );
}

type TabKey = 'episode' | 'tentang' | 'pemeran';

const PAGE = 60;

export function DetailPage() {
  const { platform, id } = useParams<{ platform: string; id: string }>();
  const navigate = useNavigate();
  const lang = useLang();

  const detailQuery = useQuery({
    queryKey: ['detail', platform, id, lang],
    queryFn: () => api.detail(platform!, id!, lang),
    enabled: Boolean(platform) && Boolean(id),
  });

  if (detailQuery.isPending) return <Skeleton />;
  if (detailQuery.isError) {
    return (
      <div className="px-4 pt-16">
        <ErrorMessage
          title="Gagal memuat detail"
          message={detailQuery.error.message}
          onRetry={() => void detailQuery.refetch()}
        />
      </div>
    );
  }

  return (
    <DetailBody
      data={detailQuery.data.data}
      onPlay={(cid) => navigate(`/player/${platform}/${id}/${cid}`)}
    />
  );
}

function DetailBody({
  data,
  onPlay,
}: {
  data: DramaDetail;
  onPlay: (chapterId: string) => void;
}) {
  const [tab, setTab] = useState<TabKey>('episode');
  const [newestFirst, setNewestFirst] = useState(false);
  const [shown, setShown] = useState(PAGE);
  const [expanded, setExpanded] = useState(false);

  const meQuery = useCurrentUser();
  const loggedIn = (meQuery.data?.user ?? null) !== null;

  // Cari episode terakhir yang ditonton untuk judul ini → CTA jadi "Lanjutkan".
  const historyQuery = useQuery({
    queryKey: ['history'],
    queryFn: () => api.history(),
    enabled: loggedIn,
  });
  const resume = (historyQuery.data?.items ?? []).find(
    (h) => h.platform === data.platform && h.externalDramaId === data.id,
  );

  const ordered = useMemo(() => {
    const list = [...data.chapters];
    return newestFirst ? list.reverse() : list;
  }, [data.chapters, newestFirst]);

  const first = data.chapters[0];
  const ctaChapter: Chapter | undefined = first;

  /**
   * Tujuan tombol utama:
   *  - belum pernah menonton      → episode pertama
   *  - episode terakhir BELUM tamat → lanjutkan episode itu (player akan
   *    meneruskan dari detik terakhir)
   *  - episode terakhir SUDAH tamat → episode berikutnya, jangan diulang
   */
  const nextAfterFinished =
    resume?.completed === true
      ? data.chapters.find((c) => c.index === resume.episodeNumber + 1)
      : undefined;

  const cta: { chapterId: string; label: string } = nextAfterFinished
    ? {
        chapterId: chapterIdForUrl(nextAfterFinished),
        label: `Tonton Episode ${nextAfterFinished.index}`,
      }
    : resume
      ? {
          chapterId: resume.externalEpisodeId,
          label: `Lanjutkan Episode ${resume.episodeNumber}`,
        }
      : {
          chapterId: ctaChapter ? chapterIdForUrl(ctaChapter) : '',
          label: 'Mulai Tonton',
        };

  // Upstream kadang mengirim "Unknown" — jangan ditampilkan sebagai fakta.
  const clean = (v: string | null) =>
    v && v.trim().toLowerCase() !== 'unknown' ? v : null;

  const meta = [
    data.platform,
    clean(data.status),
    clean(data.year),
    clean(data.country),
  ].filter(Boolean) as string[];

  return (
    <div className="min-h-screen pb-6">
      {/* Backdrop sinematik */}
      <div className="relative h-[300px] w-full overflow-hidden bg-ink-850">
        {data.cover && (
          <img
            src={data.cover}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/60 via-ink-950/10 to-ink-950" />

        <div className="absolute inset-x-1 top-0 flex items-center justify-between safe-top">
          <BackButton light />
        </div>
      </div>

      {/*
        Judul & metadata naik menimpa backdrop. `relative z-10` wajib:
        tanpa itu chip metadata bisa tertimpa lapisan gradasi backdrop.
      */}
      <div className="relative z-10 -mt-14 px-4">
        <h1 className="font-display text-[28px] font-extrabold leading-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.65)]">
          {data.title}
        </h1>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {meta.map((m, i) => (
            <span
              key={`${m}-${i}`}
              className={`rounded-md px-2.5 py-1.5 text-[11px] font-semibold ${
                i === 0
                  ? 'border border-brand-500/45 bg-brand-500/15 uppercase text-brand-400'
                  : 'bg-ink-800 text-neutral-300'
              }`}
            >
              {m}
            </span>
          ))}
        </div>

        {data.rating !== null && (
          <div className="mt-3 flex items-center gap-2">
            <svg className="text-brand-500" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.6l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5-5.8-3-5.8 3 1.1-6.5L2.6 9.4l6.5-.9L12 2.6z" />
            </svg>
            <span className="font-display text-lg font-extrabold">
              {data.rating.toFixed(1)}
            </span>
            {data.totalEpisodes !== null && (
              <span className="text-xs text-neutral-500">
                {data.totalEpisodes} episode
              </span>
            )}
          </div>
        )}

        {/* CTA utama */}
        {ctaChapter && (
          <button
            type="button"
            onClick={() => onPlay(cta.chapterId)}
            className="mt-4 flex h-[50px] w-full items-center justify-center gap-2 rounded-2xl bg-gold-grad shadow-gold"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#14100A">
              <path d="M8 5.1v13.8c0 .8.9 1.3 1.6.9l10.7-6.9a1 1 0 0 0 0-1.7L9.6 4.2A1 1 0 0 0 8 5.1z" />
            </svg>
            <span className="font-display text-[15px] font-bold text-gold-ink">
              {cta.label}
            </span>
          </button>
        )}

        {/* Aksi sekunder */}
        <div className="mt-2 grid grid-cols-2 gap-2">
          <FavoriteButton
            platform={data.platform}
            dramaId={data.id}
            title={data.title}
            poster={data.cover}
            episodeCount={data.totalEpisodes}
          />
          <ShareButton title={data.title} />
        </div>

        {/* Sinopsis */}
        {data.synopsis && (
          <div className="mt-5">
            <p
              className={`text-[13px] leading-relaxed text-neutral-400 ${
                expanded ? '' : 'line-clamp-3'
              }`}
            >
              {data.synopsis}
            </p>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 text-xs font-bold text-brand-500"
            >
              {expanded ? 'Lebih sedikit' : 'Selengkapnya'}
            </button>
          </div>
        )}

        {/* Genre */}
        {data.genres.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {data.genres.map((g) => (
              <span
                key={g}
                className="rounded-full bg-ink-800 px-3.5 py-2 text-[11.5px] font-semibold text-neutral-300"
              >
                {g}
              </span>
            ))}
          </div>
        )}

        {/* Tab */}
        <div className="mt-6 flex gap-6 border-b border-line/[0.08]">
          {([
            ['episode', `Episode (${data.chapters.length})`],
            ['tentang', 'Tentang'],
            ['pemeran', 'Pemeran'],
          ] as [TabKey, string][]).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`-mb-px border-b-2 py-3 text-[13.5px] transition ${
                tab === key
                  ? 'border-brand-500 font-bold text-brand-400'
                  : 'border-transparent font-semibold text-neutral-500'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Isi tab */}
        {tab === 'episode' && (
          <section className="mt-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[13px] font-bold">Semua Episode</span>
              <button
                type="button"
                onClick={() => {
                  setNewestFirst((v) => !v);
                  setShown(PAGE);
                }}
                className="flex min-h-[40px] items-center gap-1.5 rounded-lg bg-ink-800 px-3 text-[11.5px] font-semibold text-neutral-300"
              >
                {newestFirst ? 'Terbaru' : 'Terlama'}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
            </div>

            <div className="flex flex-col gap-2">
              {ordered.slice(0, shown).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onPlay(chapterIdForUrl(c))}
                  className="flex min-h-[56px] items-center gap-3 rounded-xl bg-ink-900 px-3 text-left transition active:scale-[0.99]"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-500/12 font-display text-[13px] font-extrabold text-brand-500">
                    {c.index}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold">
                      {c.title || `Episode ${c.index}`}
                    </span>
                    {resume?.episodeNumber === c.index && (
                      <span className="text-[10.5px] font-bold text-brand-500">
                        Terakhir ditonton
                      </span>
                    )}
                  </span>
                  <svg className="text-neutral-500" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>

            {shown < ordered.length && (
              <button
                type="button"
                onClick={() => setShown((s) => s + PAGE)}
                className="mt-3 h-11 w-full rounded-xl border border-brand-500/35 text-[13px] font-bold text-brand-400"
              >
                Muat {Math.min(PAGE, ordered.length - shown)} episode lagi
              </button>
            )}
          </section>
        )}

        {tab === 'tentang' && (
          <section className="mt-4 flex flex-col gap-3 text-[13px]">
            {data.synopsis && (
              <p className="leading-relaxed text-neutral-400">{data.synopsis}</p>
            )}
            <dl className="flex flex-col gap-2.5 rounded-xl bg-ink-900 p-4">
              {[
                ['Platform', data.platform],
                ['Status', data.status],
                ['Tahun', data.year],
                ['Negara', data.country],
                ['Sutradara', data.director],
                ['Penulis', data.author],
                ['Total episode', data.totalEpisodes?.toString() ?? null],
              ]
                .filter(([, v]) => Boolean(v))
                .map(([k, v]) => (
                  <div key={k as string} className="flex justify-between gap-4">
                    <dt className="shrink-0 text-neutral-500">{k}</dt>
                    <dd className="text-right font-semibold text-neutral-200">
                      {v}
                    </dd>
                  </div>
                ))}
            </dl>
          </section>
        )}

        {tab === 'pemeran' && (
          <section className="mt-4">
            {data.actors.length === 0 ? (
              <p className="rounded-xl bg-ink-900 p-4 text-[13px] text-neutral-500">
                Data pemeran belum tersedia untuk judul ini.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.actors.map((a) => (
                  <li
                    key={a}
                    className="flex min-h-[52px] items-center gap-3 rounded-xl bg-ink-900 px-3"
                  >
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-500/12 font-display text-[12px] font-extrabold uppercase text-brand-500">
                      {a.slice(0, 2)}
                    </span>
                    <span className="text-[13px] font-semibold text-neutral-200">
                      {a}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
